import * as THREE from 'three/webgpu';

/**
 * @file Sampled terrain. A regular grid of heights with bilinear lookup, plus a
 * flat-shaded non-indexed mesh builder. Forest (fbm dunes, cell 1.5m, amp 9m)
 * and Wasteland (ridged dunes) both stand on this; City just uses a constant
 * `heightAt`.
 *
 * `heightAt` is called PER ACTOR PER FRAME, so it is deliberately branch-light
 * and allocation-free.
 *
 * @module engine/world/Heightfield
 */

/** @typedef {import('../types.js').Bounds2} Bounds2 */

export class Heightfield {
  /**
   * Prefer {@link Heightfield.from}. Direct construction is for deserialising a
   * field that was generated elsewhere (a worker, a cache).
   * @param {Float32Array} data `(nx+1) * (nz+1)` samples, row-major in Z.
   * @param {Bounds2} bounds
   * @param {number} cell
   * @param {number} nx Cell count along X (so `nx+1` samples).
   * @param {number} nz Cell count along Z.
   */
  constructor(data, bounds, cell, nx, nz) {
    this.data = data;
    this.bounds = bounds;
    this.cell = cell;
    this.nx = nx;
    this.nz = nz;
    this.minX = bounds.minX;
    this.minZ = bounds.minZ;

    /** @type {number} Lowest sample. Kept current by {@link refreshExtent}. */
    this.minY = 0;
    /** @type {number} Highest sample. Kept current by {@link refreshExtent}. */
    this.maxY = 0;
    this.refreshExtent();

    // Bound the height sampler so `heightAt` never allocates or branches on it.
    this.heightAt = this.heightAt.bind(this);
  }

  /**
   * Re-derives `minY` / `maxY` from `data`.
   *
   * Levels MUTATE `field.data` in place after construction — the forest carves
   * its path and flattens its clearings, the wasteland cuts its highway through
   * the dunes — and the extent those mutations move is read by the terrain
   * albedo blend, the fog skirt height and the camera. This is the invalidation
   * hook for that; call it once after the last edit, before anything measures
   * the field.
   *
   * The constructor calls it, so a freshly built field is already correct.
   *
   * @returns {this}
   */
  refreshExtent() {
    const d = this.data;
    let lo = Infinity;
    let hi = -Infinity;
    for (let i = 0; i < d.length; i++) {
      if (d[i] < lo) lo = d[i];
      if (d[i] > hi) hi = d[i];
    }
    this.minY = d.length ? lo : 0;
    this.maxY = d.length ? hi : 0;
    return this;
  }

  /**
   * Samples `fn(x, z)` on a grid covering `bounds`.
   * @param {(x: number, z: number) => number} fn Deterministic height function
   *   (seeded noise — never `Math.random`).
   * @param {Bounds2} bounds
   * @param {number} [cell=1.5] Metres between samples. The plan's forest uses
   *   1.5; larger is cheaper and blockier.
   * @returns {Heightfield}
   */
  static from(fn, bounds, cell = 1.5) {
    const c = cell > 0 ? cell : 1.5;
    const nx = Math.max(1, Math.ceil((bounds.maxX - bounds.minX) / c));
    const nz = Math.max(1, Math.ceil((bounds.maxZ - bounds.minZ) / c));
    const data = new Float32Array((nx + 1) * (nz + 1));
    for (let j = 0; j <= nz; j++) {
      const z = bounds.minZ + j * c;
      for (let i = 0; i <= nx; i++) {
        data[j * (nx + 1) + i] = fn(bounds.minX + i * c, z);
      }
    }
    return new Heightfield(data, bounds, c, nx, nz);
  }

  /**
   * Height at a sample-grid node (clamped at the edges).
   * @param {number} i @param {number} j
   * @returns {number}
   */
  at(i, j) {
    const a = i < 0 ? 0 : i > this.nx ? this.nx : i;
    const b = j < 0 ? 0 : j > this.nz ? this.nz : j;
    return this.data[b * (this.nx + 1) + a];
  }

  /**
   * Bilinear height. Outside the bounds it clamps to the edge (no extrapolation
   * cliffs at the world border).
   * @param {number} x
   * @param {number} z
   * @returns {number}
   */
  heightAt(x, z) {
    const fx = (x - this.minX) / this.cell;
    const fz = (z - this.minZ) / this.cell;
    let i = Math.floor(fx);
    let j = Math.floor(fz);
    let tx = fx - i;
    let tz = fz - j;
    if (i < 0) {
      i = 0;
      tx = 0;
    } else if (i >= this.nx) {
      i = this.nx - 1;
      tx = 1;
    }
    if (j < 0) {
      j = 0;
      tz = 0;
    } else if (j >= this.nz) {
      j = this.nz - 1;
      tz = 1;
    }
    const w = this.nx + 1;
    const o = j * w + i;
    const h00 = this.data[o];
    const h10 = this.data[o + 1];
    const h01 = this.data[o + w];
    const h11 = this.data[o + w + 1];
    const a = h00 + (h10 - h00) * tx;
    const b = h01 + (h11 - h01) * tx;
    return a + (b - a) * tz;
  }

  /**
   * Smooth surface normal from central differences of the bilinear field. Used
   * for slope-based albedo blending (grass -> dirt > 30 deg -> rock > 48 deg)
   * and for orienting scattered props.
   * @param {number} x
   * @param {number} z
   * @param {{x: number, y: number, z: number}} [out] Reused if given.
   * @returns {{x: number, y: number, z: number}}
   */
  normalAt(x, z, out = { x: 0, y: 1, z: 0 }) {
    const e = this.cell * 0.5;
    const hx = (this.heightAt(x + e, z) - this.heightAt(x - e, z)) / (2 * e);
    const hz = (this.heightAt(x, z + e) - this.heightAt(x, z - e)) / (2 * e);
    const l = Math.hypot(hx, 1, hz) || 1;
    out.x = -hx / l;
    out.y = 1 / l;
    out.z = -hz / l;
    return out;
  }

  /**
   * Builds the terrain mesh: non-indexed, two triangles per cell.
   *
   * The `color` attribute is filled with WHITE — tinting is the CALLER's job
   * (slope/height albedo blend), done before handing the geometry to the bake.
   * That keeps this class free of level-specific palette knowledge.
   *
   * UVs are planar XZ in metres (retile with `uvtools.scaleUV`).
   *
   * @param {{ flat?: boolean, materialKey?: string }} [opts]
   *   `flat` (default true) writes exact per-face normals — the low-poly look.
   *   `flat:false` writes smooth per-vertex normals from {@link normalAt}, which
   *   is occasionally wanted for water or distant LOD.
   *   `materialKey` is stashed on `geometry.userData.materialKey` so the chunker
   *   and the level's material resolver can find it.
   * @returns {THREE.BufferGeometry} `.index === null`.
   */
  toGeometry({ flat = true, materialKey = 'ground' } = {}) {
    const { nx, nz, cell, minX, minZ } = this;
    const triCount = nx * nz * 2;
    const n = triCount * 3;
    const pos = new Float32Array(n * 3);
    const nor = new Float32Array(n * 3);
    const uv = new Float32Array(n * 2);
    const col = new Float32Array(n * 3).fill(1);

    let w = 0;
    const put = (px, py, pz, nxv, nyv, nzv) => {
      const p = w * 3;
      const q = w * 2;
      pos[p] = px;
      pos[p + 1] = py;
      pos[p + 2] = pz;
      nor[p] = nxv;
      nor[p + 1] = nyv;
      nor[p + 2] = nzv;
      uv[q] = px;
      uv[q + 1] = pz;
      w++;
    };

    const tmp = { x: 0, y: 1, z: 0 };

    for (let j = 0; j < nz; j++) {
      for (let i = 0; i < nx; i++) {
        const x0 = minX + i * cell;
        const x1 = x0 + cell;
        const z0 = minZ + j * cell;
        const z1 = z0 + cell;
        const h00 = this.at(i, j);
        const h10 = this.at(i + 1, j);
        const h01 = this.at(i, j + 1);
        const h11 = this.at(i + 1, j + 1);

        // Two triangles, wound so their normals point +Y.
        // (x0,z1) (x1,z1) (x1,z0)  and  (x0,z1) (x1,z0) (x0,z0)
        emitTri(x0, h01, z1, x1, h11, z1, x1, h10, z0);
        emitTri(x0, h01, z1, x1, h10, z0, x0, h00, z0);
      }
    }

    /**
     * @param {number} ax @param {number} ay @param {number} az
     * @param {number} bx @param {number} by @param {number} bz
     * @param {number} cx @param {number} cy @param {number} cz
     */
    function emitTri(ax, ay, az, bx, by, bz, cx, cy, cz) {
      if (flat) {
        const ux = bx - ax;
        const uy = by - ay;
        const uz = bz - az;
        const vx = cx - ax;
        const vy = cy - ay;
        const vz = cz - az;
        let fx = uy * vz - uz * vy;
        let fy = uz * vx - ux * vz;
        let fz = ux * vy - uy * vx;
        const l = Math.hypot(fx, fy, fz) || 1;
        fx /= l;
        fy /= l;
        fz /= l;
        put(ax, ay, az, fx, fy, fz);
        put(bx, by, bz, fx, fy, fz);
        put(cx, cy, cz, fx, fy, fz);
      } else {
        put(ax, ay, az, 0, 1, 0);
        put(bx, by, bz, 0, 1, 0);
        put(cx, cy, cz, 0, 1, 0);
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));

    if (!flat) {
      // Smooth pass: resample the analytic normal at each emitted vertex.
      const P = pos;
      for (let i = 0; i < n; i++) {
        this.normalAt(P[i * 3], P[i * 3 + 2], tmp);
        nor[i * 3] = tmp.x;
        nor[i * 3 + 1] = tmp.y;
        nor[i * 3 + 2] = tmp.z;
      }
    }

    geo.computeBoundingBox();
    geo.computeBoundingSphere();
    geo.userData.materialKey = materialKey;
    if (geo.index !== null) throw new Error('Heightfield.toGeometry: produced an indexed geometry');
    return geo;
  }

  /**
   * @returns {{nx: number, nz: number, cell: number, samples: number, tris: number, minY: number, maxY: number}}
   */
  stats() {
    return {
      nx: this.nx,
      nz: this.nz,
      cell: this.cell,
      samples: this.data.length,
      tris: this.nx * this.nz * 2,
      minY: this.minY,
      maxY: this.maxY,
    };
  }
}
